import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';

import { useHistory } from 'react-router-dom';
import { captureMessage } from '@tink/core/src/sentry';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { PageView } from '../../../../../../models/analytics';
import { VerboseErrorScreen } from '../../../../../../components/screens/VerboseErrorScreen/VerboseErrorScreen';
import { setApplicationError as setApplicationErrorImport } from '../../../../../../stores/app-state-store';
import { ProviderEntity } from '../../../../../../models/provider';

import { buildSingleProviderUrl } from '../../../../../../routes';
import { useVersion } from '../../../../../../components/ApplicationProviders/VersionProvider/VersionProvider';
import { resetCredentials as resetCredentialsConnect } from '../../../../../../stores/credentials-store';
import { useProduct } from '../../../../../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { messages as AccountErrorOverlayMessages } from '../../../../../components/AccountErrorOverlay/messages';
import { messages } from './messages';

export type SwedbankSavingsbankErrorOverlayOwnProps = {
  provider: ProviderEntity;
};

export type SwedbankSavingsbankErrorOverlayProps = PropsFromRedux &
  SwedbankSavingsbankErrorOverlayOwnProps;

enum SwedbankOrSavingsbank {
  SWEDBANK = 'swedbank-bankid',
  SAVINGSBANK = 'savingsbank-bankid',
  SWEDBANK_FALLBACK = 'se-swedbank-fallback',
  SAVINGSBANK_FALLBACK = 'se-savingsbank-fallback',
}

export const isSwedbankOrSavingsbank = (providerName: string | undefined) => {
  return providerName && (Object.values(SwedbankOrSavingsbank) as string[]).includes(providerName);
};

const getCounterpartProviderName = (providerName: SwedbankOrSavingsbank) => {
  if (
    providerName === SwedbankOrSavingsbank.SWEDBANK ||
    providerName === SwedbankOrSavingsbank.SWEDBANK_FALLBACK
  ) {
    return 'Sparbanken';
  }

  return 'Swedbank';
};

export const SwedbankSavingsbankErrorOverlayPresenter: React.FC<
  SwedbankSavingsbankErrorOverlayProps
> = ({ setApplicationError, provider, resetCredentials }) => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const {
    version: { version },
  } = useVersion();
  const { productType, productDimension } = useProduct();
  const providerName = getCounterpartProviderName(provider.name as SwedbankOrSavingsbank);

  const primaryButtonAction = useCallback(() => {
    let counterpartProviderName;
    switch (provider.name as SwedbankOrSavingsbank) {
      case SwedbankOrSavingsbank.SWEDBANK:
        counterpartProviderName = SwedbankOrSavingsbank.SAVINGSBANK;
        break;
      case SwedbankOrSavingsbank.SAVINGSBANK:
        counterpartProviderName = SwedbankOrSavingsbank.SWEDBANK;
        break;
      case SwedbankOrSavingsbank.SWEDBANK_FALLBACK:
        counterpartProviderName = SwedbankOrSavingsbank.SAVINGSBANK_FALLBACK;
        break;
      case SwedbankOrSavingsbank.SAVINGSBANK_FALLBACK:
        counterpartProviderName = SwedbankOrSavingsbank.SWEDBANK_FALLBACK;
        break;
      default:
        break;
    }

    if (counterpartProviderName) {
      history.push(
        buildSingleProviderUrl({
          version,
          productType,
          productDimension,
          providerName: counterpartProviderName,
        })
      );
    } else {
      captureMessage('No Swedbank/Savingsbank provider was found');
    }

    resetCredentials();
  }, [provider.name, history, version, resetCredentials, productType, productDimension]);

  const secondaryButtonAction = useCallback(() => {
    setApplicationError({
      errorStatus: ErrorStatus.AUTHENTICATION_ERROR,
      errorReason: ErrorReason.INVALID_STATE_PROVIDER_DOES_NOT_RECOGNIZE_USER,
    });
  }, [setApplicationError]);

  return (
    <VerboseErrorScreen
      viewEvent={PageView.SWEDBANK_SAVINGSBANK_ERROR_SCREEN}
      title={formatMessage(messages.title, { providerName: provider.displayName })}
      body={[{ text: formatMessage(messages.text, { providerName }) }]}
      primaryAction={primaryButtonAction}
      primaryButtonText={formatMessage(messages.primaryButtonLabel, {
        providerName,
      })}
      secondaryAction={secondaryButtonAction}
      secondaryButtonText={formatMessage(AccountErrorOverlayMessages.bankSelection)}
    />
  );
};

const mapDispatchToProps = {
  setApplicationError: setApplicationErrorImport,
  resetCredentials: resetCredentialsConnect,
};

const connector = connect(null, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const SwedbankSavingsbankErrorOverlay = connector(SwedbankSavingsbankErrorOverlayPresenter);
